/**
 * Supplementary Materials Mock Data Panel
 *
 * 开发环境下的快捷上传工具
 * 提供可视化的面板用于快速上传补充材料文件
 *
 * @module mockDataGenerator.demo
 */

import { delay } from "@/utils";
import { triggerFileUpload } from "../utils/mockFileUploadTrigger";
import { Button, InputNumber, message } from "antd";
import { FormInstance } from "antd/lib/form";
import { useState } from "react";

interface SupplementaryMaterialsMockDataPanelProps {
  form: FormInstance;
}

/**
 * 补充材料 Mock 数据生成工具面板
 */
export function SupplementaryMaterialsMockDataPanel({
  form,
}: SupplementaryMaterialsMockDataPanelProps) {
  const [visible, setVisible] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fileCount, setFileCount] = useState(5);

  if (!form) {
    return null;
  }

  /**
   * 上传指定数量的文件（只上传一次，复用fileId）
   */
  const handleUploadFiles = async (count: number) => {
    setUploading(true);
    message.loading({
      content: "正在上传文件...",
      key: "upload-files",
      duration: 0,
    });

    try {
      // 步骤1：触发一次上传
      console.log("🔄 触发文件上传...");
      await triggerFileUpload({
        fileUrl: "/icon.png",
        fileName: "supplementary-material.png",
        selector: 'input[type="file"]',
      });

      // 步骤2：等待上传完成
      message.loading({
        content: "等待上传完成...",
        key: "upload-files",
        duration: 0,
      });

      let uploadedFile: any = null;
      const maxAttempts = 20;

      for (let i = 0; i < maxAttempts; i++) {
        await delay(500);

        const currentFiles = form.getFieldValue("supplementaryFiles");
        if (
          currentFiles &&
          currentFiles.length > 0 &&
          currentFiles[0]?.fileId
        ) {
          uploadedFile = currentFiles[0];
          console.log("✅ 上传成功，fileId:", uploadedFile.fileId);
          break;
        }

        console.log(`⏳ 等待上传完成... (${i + 1}/${maxAttempts})`);
      }

      if (!uploadedFile || !uploadedFile.fileId) {
        throw new Error("上传超时，未获取到fileId");
      }

      // 步骤3：复制N次设置到表单
      message.loading({
        content: `正在设置 ${count} 个文件...`,
        key: "upload-files",
        duration: 0,
      });

      const files = Array(count).fill(uploadedFile);
      form.setFieldsValue({
        supplementaryFiles: files,
      });

      message.success({
        content: `✅ 已设置 ${count} 个文件`,
        key: "upload-files",
        duration: 3,
      });
      console.log(`✅ 已设置 ${count} 个文件，fileId:`, uploadedFile.fileId);
    } catch (error) {
      message.error({
        content: "❌ 上传失败",
        key: "upload-files",
        duration: 3,
      });
      console.error("❌ 上传失败:", error);
    } finally {
      setUploading(false);
    }
  };

  /**
   * 上传1个文件
   */
  const handleUpload1 = () => handleUploadFiles(1);

  /**
   * 上传5个文件
   */
  const handleUpload5 = () => handleUploadFiles(5);

  /**
   * 上传10个文件
   */
  const handleUpload10 = () => handleUploadFiles(10);

  /**
   * 上传自定义数量的文件
   */
  const handleUploadCustom = () => handleUploadFiles(fileCount);

  const handleToggle = () => {
    setVisible(!visible);
  };

  return (
    <>
      {/* 浮动按钮 */}
      {!visible && (
        <div
          className="fixed bottom-8 right-8 z-[9999] group cursor-pointer"
          onClick={handleToggle}
          style={{
            width: "64px",
            height: "64px",
          }}
        >
          <div
            className="absolute inset-0 rounded-full transition-all duration-300 group-hover:scale-110"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              boxShadow:
                "0 10px 25px -5px rgba(102, 126, 234, 0.5), 0 8px 10px -6px rgba(102, 126, 234, 0.3)",
            }}
          />
          <div
            className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            style={{
              background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
              boxShadow: "0 15px 35px -5px rgba(118, 75, 162, 0.6)",
            }}
          />
          <div className="relative w-full h-full flex items-center justify-center text-3xl transition-transform duration-300 group-hover:scale-110 group-active:scale-95">
            📄
          </div>
          <div
            className="absolute inset-0 rounded-full animate-ping opacity-20"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              animationDuration: "3s",
            }}
          />
        </div>
      )}

      {/* 主面板 */}
      {visible && (
        <div
          className="fixed bottom-8 right-8 z-[9999] bg-white rounded-lg shadow-2xl"
          style={{
            width: "320px",
            maxHeight: "600px",
            display: "flex",
            flexDirection: "column",
            border: "1px solid #d9d9d9",
          }}
        >
          {/* 标题栏 */}
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{
              borderBottom: "1px solid #f0f0f0",
              backgroundColor: "#fafafa",
              borderRadius: "8px 8px 0 0",
            }}
          >
            <span className="font-semibold text-base">
              📄 补充材料 Mock工具
            </span>
            <Button size="small" onClick={handleToggle}>
              ✕
            </Button>
          </div>

          {/* 内容区域 */}
          <div
            className="overflow-y-auto p-4 space-y-4"
            style={{ maxHeight: "500px" }}
          >
            {/* 快捷上传 */}
            <div className="space-y-2">
              <div className="text-sm font-semibold text-gray-700">
                🚀 快捷上传
              </div>

              <Button
                size="small"
                block
                type="primary"
                onClick={handleUpload1}
                loading={uploading}
              >
                上传 1 个文件
              </Button>

              <Button
                size="small"
                block
                type="primary"
                onClick={handleUpload5}
                loading={uploading}
              >
                上传 5 个文件
              </Button>

              <Button
                size="small"
                block
                type="primary"
                onClick={handleUpload10}
                loading={uploading}
              >
                上传 10 个文件（最大）
              </Button>
            </div>

            {/* 分割线 */}
            <div className="border-t border-gray-200" />

            {/* 自定义数量 */}
            <div className="space-y-2">
              <div className="text-sm font-semibold text-gray-700">
                🔢 自定义数量
              </div>

              <div className="flex items-center gap-2">
                <InputNumber
                  size="small"
                  min={1}
                  max={10}
                  value={fileCount}
                  onChange={(val) => setFileCount(val || 1)}
                  style={{ width: "100px" }}
                  addonAfter="个"
                />
                <Button
                  size="small"
                  onClick={handleUploadCustom}
                  loading={uploading}
                  type="primary"
                >
                  上传
                </Button>
              </div>

              <div className="text-xs text-gray-500">
                可选范围: 1 - 10 个文件
              </div>
            </div>

            {/* 分割线 */}
            <div className="border-t border-gray-200" />

            {/* 说明 */}
            <div className="space-y-2">
              <div className="text-sm font-semibold text-gray-700">
                ℹ️ 使用说明
              </div>
              <div className="text-xs text-gray-600 space-y-1">
                <div>• 每个文件都会上传同一张图片（/icon.png）</div>
                <div>• 最多可上传 10 个文件</div>
                <div>• 上传完成后可在表单中查看</div>
                <div>• 支持自定义上传数量</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default SupplementaryMaterialsMockDataPanel;
